import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FiUsers, FiSearch, FiExternalLink } from "react-icons/fi";
import { workshopApi } from "../../services/workshopApi";
import "./ParticipantList.css";

// Animation Variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.04 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.25, ease: "easeOut" },
  },
};

// Helper function to format date
const formatJoinedDate = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

// Participant Card Component - Enhanced with enriched data
const ParticipantCard = ({ participant }) => {
  const navigate = useNavigate();
  const user = participant.user || participant;
  const joinedAt = participant.joinedAt || participant.approvedAt;

  const handleClick = () => {
    const userId = user._id || user.id;
    if (userId) {
      navigate(`/profile/${userId}`);
    }
  };

  return (
    <motion.div
      className="wpl-card"
      variants={itemVariants}
      onClick={handleClick}
    >
      <div className="wpl-avatar">
        {user.profilePhotoUrl ? (
          <img
            src={user.profilePhotoUrl}
            alt={user.name}
            onError={(e) => {
              e.target.style.display = "none";
              e.target.nextSibling.style.display = "flex";
            }}
          />
        ) : null}
        <span className="wpl-avatar-fallback">
          {user.name?.charAt(0)?.toUpperCase() || "?"}
        </span>
      </div>
      <div className="wpl-info">
        <span className="wpl-name">{user.name || "Unknown"}</span>
        {user.professionalRole && (
          <span className="wpl-role">{user.professionalRole}</span>
        )}
        {user.field && (
          <span className="wpl-field">{user.field}</span>
        )}
        {joinedAt && (
          <span className="wpl-joined">Joined {formatJoinedDate(joinedAt)}</span>
        )}
      </div>
      <FiExternalLink className="wpl-link-icon" />
    </motion.div>
  );
};


// Main Component
const ParticipantList = ({ workshopId }) => {
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const debounceRef = useRef(null);

  // Fetch participants
  const fetchParticipants = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // Use the same API but filtered for approved participants only
      const response = await workshopApi.getWorkshopParticipants(workshopId, {
        status: "approved",
      });
      if (response.success) {
        setParticipants(response.data.participants || []);
      }
    } catch (err) {
      setError(err.message);
} finally {
      setLoading(false);
    }
  }, [workshopId]);

  useEffect(() => {
    fetchParticipants();
  }, [fetchParticipants]);

  // Debounced search filter - searches by name, role, and field
  const filteredParticipants = participants.filter((p) => {
    if (!searchQuery.trim()) return true;
    const user = p.user || p;
    const name = user.name?.toLowerCase() || "";
    const role = user.professionalRole?.toLowerCase() || "";
    const field = user.field?.toLowerCase() || "";
    const query = searchQuery.toLowerCase();
    return name.includes(query) || role.includes(query) || field.includes(query);
  });

  // Handle search with debounce
  const handleSearchChange = (e) => {
    const value = e.target.value;
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      setSearchQuery(value);
    }, 300);
  };

  // Loading State
  if (loading) {
    return (
      <div className="wpl-container">
        <div className="wpl-header">
          <div className="wpl-header-left">
            <div className="wpl-header-icon">
              <FiUsers />
            </div>
            <div>
              <h2 className="wpl-header-title">Participants</h2>
              <span className="wpl-header-count">Loading...</span>
            </div>
          </div>
        </div>
        <div className="wpl-loading-grid">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="wpl-skeleton-card">
              <div className="wpl-skeleton wpl-skeleton-avatar" />
              <div className="wpl-skeleton-content">
                <div className="wpl-skeleton wpl-skeleton-name" />
                <div className="wpl-skeleton wpl-skeleton-role" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="wpl-container">
        <div className="wpl-error">
          <p>Failed to load participants: {error}</p>
          <button className="wpl-retry-btn" onClick={fetchParticipants}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="wpl-container">
      {/* Header */}
      <div className="wpl-header">
        <div className="wpl-header-left">
          <div className="wpl-header-icon">
            <FiUsers />
          </div>
          <div>
            <h2 className="wpl-header-title">Participants</h2>
            <span className="wpl-header-count">
              {participants.length} enrolled
            </span>
          </div>
        </div>
      </div>

      {/* Search */}
      {participants.length > 5 && (
        <div className="wpl-search-wrapper">
          <FiSearch className="wpl-search-icon" />
          <input
            type="text"
            className="wpl-search-input"
            placeholder="Search participants..."
            onChange={handleSearchChange}
          />
        </div>
      )}

      {/* Participants Grid */}
      {filteredParticipants.length === 0 ? (
        <div className="wpl-empty">
          <FiUsers className="wpl-empty-icon" />
          <h3>No participants found</h3>
          <p>
            {searchQuery
              ? "Try a different search term"
              : "No one has joined this workshop yet"}
          </p>
        </div>
      ) : (
        <motion.div
          className="wpl-grid"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {filteredParticipants.map((participant) => (
            <ParticipantCard
              key={participant._id || participant.user?._id}
              participant={participant}
            />
          ))}
        </motion.div>
      )}
    </div>
  );
};

export default ParticipantList;
